import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

import type { FlowFeed, FlowStack } from '../components/GatewaySignalFlow';

export interface ResourceStacks {
  /** `<home-tricode>.<league-alias>.ingest.cxvh.cosm.com`, or null if not derivable. */
  hostname: string | null;
  /**
   * The game's cameras, plus any feed TM already has a config for, plus any
   * feed the scheduler has queued for launch — the last of those carries
   * `planned`.
   */
  feeds: FlowFeed[];
  /**
   * What TM last reported per config. Duplicated per feed for the usual reason
   * — TM makes a config per launch rather than upserting by name — so the
   * panel runs them through `resolveTmStack`.
   */
  tmStacks: FlowStack[];
}

/**
 * A game's feed table and per-stack TiledMedia state.
 *
 * Separate from the resource-state query the card already runs: that one is
 * served from a 5-minute Redis cache, which is fine for "gateway launched" and
 * wrong for "is camera 3 up right now".
 *
 * `enabled` is the point of the hook's shape. The Resources page renders a card
 * per game, so an always-on second poll would multiply this across every game
 * of the day; the card only asks once its flow panel is open.
 */
export const useResourceStacksQuery = (
  gameUniqueId: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['resourceStacks', gameUniqueId],
    queryFn: async () => {
      const { data } = await get<ResourceStacks>(
        `/v1.0/games/${gameUniqueId}/resources/stacks`,
      );
      return data;
    },
    enabled,
    refetchInterval: 10000,
  });
